import { type ReactNode, useMemo, useRef } from "react";

import { cn } from "@/shared/lib/utils";

import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../primitives/select";

type ColocaSelectOption = {
  value: string;
  label: ReactNode;
  disabled?: boolean;
};

type ColocaSelectProps = {
  value: string;
  onValueChange: (value: string) => void;
  options: ColocaSelectOption[];
  placeholder?: ReactNode;
  disabled?: boolean;
  id?: string;
  className?: string;
  /** Trigger height. `sm` (h-8) fits dense contexts such as the calendar caption. */
  size?: "sm" | "default";
  "aria-label"?: string;
  "aria-labelledby"?: string;
  "aria-describedby"?: string;
  "aria-invalid"?: boolean;
};

// Standalone wrapper around the Base UI `Select*` primitives. Use
// `ColocaSelectField` when working inside a React Hook Form context.
export function ColocaSelect({
  value,
  onValueChange,
  options,
  placeholder,
  disabled,
  id,
  className,
  size,
  ...rest
}: ColocaSelectProps) {
  // Base UI's SelectValue renders the raw value string by default — pass a render function so the trigger
  // shows the matching option's label instead. Without this, controlled selects like the wallet filters
  // display "all" instead of the translated "Todos los tipos".
  const labelByValue = useMemo(() => {
    const map = new Map<string, ReactNode>();
    for (const option of options) map.set(option.value, option.label);
    return map;
  }, [options]);

  // Lets the popup open at the trigger's width instead of widening a frame later. See `SelectContent`.
  const triggerRef = useRef<HTMLButtonElement | null>(null);

  return (
    <Select value={value} onValueChange={(next) => onValueChange(next ?? "")} disabled={disabled}>
      <SelectTrigger
        ref={triggerRef}
        id={id}
        size={size}
        className={cn("w-full", className)}
        aria-label={rest["aria-label"]}
        aria-labelledby={rest["aria-labelledby"]}
        aria-describedby={rest["aria-describedby"]}
        aria-invalid={rest["aria-invalid"]}
      >
        <SelectValue placeholder={placeholder}>
          {(selected: string) => labelByValue.get(selected) ?? placeholder ?? selected}
        </SelectValue>
      </SelectTrigger>
      <SelectContent anchorRef={triggerRef}>
        {options.map((option) => (
          <SelectItem key={option.value} value={option.value} disabled={option.disabled}>
            {option.label}
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  );
}
